import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, Mail } from 'lucide-react';
import { useSettings } from '../App';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SocialIcon = ({ platform }) => {
  switch (platform.toLowerCase()) {
    case 'instagram': return <Instagram size={20} />;
    case 'youtube': return <Youtube size={20} />;
    case 'facebook': return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
    case 'tiktok': return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>;
    case 'pinterest': return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.38.04-3.4l1.46-6.17s-.37-.75-.37-1.86c0-1.74 1-3.04 2.26-3.04 1.07 0 1.58.8 1.58 1.76 0 1.07-.68 2.67-1.03 4.15-.3 1.24.62 2.25 1.84 2.25 2.21 0 3.9-2.33 3.9-5.7 0-2.98-2.14-5.06-5.2-5.06-3.54 0-5.62 2.66-5.62 5.4 0 1.07.41 2.22.93 2.85.1.12.12.23.08.35l-.35 1.4c-.05.22-.18.27-.4.16-1.49-.7-2.42-2.87-2.42-4.62 0-3.76 2.73-7.21 7.88-7.21 4.14 0 7.35 2.95 7.35 6.88 0 4.1-2.59 7.41-6.18 7.41-1.21 0-2.34-.63-2.73-1.37l-.74 2.83c-.27 1.03-1 2.33-1.49 3.12A12 12 0 1 0 12 0z"/></svg>;
    default: return null;
  }
};

const Footer = () => {
  const { settings } = useSettings();
  const [socialMedia, setSocialMedia] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subError, setSubError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [socialRes, docsRes] = await Promise.all([
          axios.get(`${API}/social`),
          axios.get(`${API}/documents`)
        ]);
        setSocialMedia(socialRes.data.filter(s => s.is_active && s.show_in_footer));
        setDocuments(docsRes.data.filter(d => d.is_active));
      } catch (e) {}
    };
    fetchData();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubError('');
    try {
      await axios.post(`${API}/newsletter/subscribe?email=${encodeURIComponent(email)}`);
      setSubscribed(true);
      setEmail('');
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) setSubError('Zbyt wiele prób. Spróbuj za godzinę.');
      else if (status === 422) setSubError('Nieprawidłowy adres e-mail.');
      else setSubError('Wystąpił błąd. Spróbuj ponownie.');
    }
  };

  return (
    <footer className="bg-[#050505] border-t border-white/5 py-12" data-testid="footer">
      <div className="container-custom">
        {/* Newsletter */}
        <div className="text-center mb-12">
          <p className="text-[#9CA3AF] text-sm mb-4">Obserwuj nas po więcej praktycznej wiedzy</p>
          <div className="flex justify-center gap-4 mb-8">
            {socialMedia.map((social) => (
              <a key={social.id} href={social.url || '#'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0B0F0E] border border-white/10 text-[#9CA3AF] hover:text-[#10b981] hover:border-[#10b981] transition-all" data-testid={`footer-social-${social.platform.toLowerCase()}`}>
                <SocialIcon platform={social.platform} />
              </a>
            ))}
          </div>
          
          {!subscribed ? (
            <div className="max-w-md mx-auto">
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 justify-center">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Twój adres e-mail" className="flex-1 bg-[#0B0F0E] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#6B7280] focus:border-[#10b981] focus:outline-none" required data-testid="newsletter-email" />
                <button type="submit" className="btn-primary py-3 px-6" data-testid="newsletter-submit">
                  <Mail size={18} />
                  Zapisz się
                </button>
              </form>
              {subError && <p className="text-red-400 text-sm mt-2 text-center">{subError}</p>}
            </div>
          ) : (
            <p className="text-[#10b981]">✓ Dziękujemy za zapis!</p>
          )}
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-white/5">
          <p className="text-[#6B7280] text-sm">
            © {new Date().getFullYear()} {settings.site_name}. Wszelkie prawa zastrzeżone.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            {documents.map((doc) => (
              <Link key={doc.id} to={`/dokument/${doc.slug}`} className="text-[#6B7280] hover:text-[#10b981] text-sm transition-colors" data-testid={`footer-link-${doc.slug}`}>
                {doc.title}
              </Link>
            ))}
            <Link to="/kontakt" className="text-[#6B7280] hover:text-[#10b981] text-sm transition-colors" data-testid="footer-link-kontakt">
              Kontakt
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;